import { COLOR_PALETTES } from '@/config/colors';
import { ICON_REGISTRY } from '@/config/serviceIcons';
import type { AccentPalette, BusinessTemplate, IllustrationComponent } from '@/types';
import { BUSINESS_TEMPLATES } from './businessTemplates';
import { getIllustration } from './illustrations';

const TEMPLATE_INDEX: ReadonlyMap<string, BusinessTemplate> = new Map(
  BUSINESS_TEMPLATES.map((template) => [template.id, template]),
);

export const FALLBACK_TEMPLATE: BusinessTemplate =
  TEMPLATE_INDEX.get('other') ?? (BUSINESS_TEMPLATES[0] as BusinessTemplate);

export function getTemplate(id: string | undefined): BusinessTemplate {
  if (!id) return FALLBACK_TEMPLATE;
  return TEMPLATE_INDEX.get(id) ?? FALLBACK_TEMPLATE;
}

export function listTemplates(): readonly BusinessTemplate[] {
  return BUSINESS_TEMPLATES;
}

export function getTemplatePalette(id: string | undefined): AccentPalette {
  return COLOR_PALETTES[getTemplate(id).color];
}

export function getTemplateIllustration(id: string | undefined): IllustrationComponent {
  return getIllustration(getTemplate(id).illustration);
}

/**
 * Dev-time guard. A new vertical that references a missing category, colour or
 * icon token fails loudly at boot instead of rendering a broken card.
 */
export function assertRegistryIsValid(): void {
  if (!import.meta.env.DEV) return;

  const seenTemplates = new Set<string>();
  const seenServices = new Set<string>();

  for (const template of BUSINESS_TEMPLATES) {
    if (seenTemplates.has(template.id)) {
      throw new Error(`[templates] Duplicate template id "${template.id}".`);
    }
    seenTemplates.add(template.id);

    if (!COLOR_PALETTES[template.color]) {
      throw new Error(`[templates] "${template.id}" uses unknown colour "${template.color}".`);
    }

    const categoryIds = new Set(template.categories.map((category) => category.id));
    if (categoryIds.size !== template.categories.length) {
      throw new Error(`[templates] "${template.id}" has duplicate category ids.`);
    }

    for (const category of template.categories) {
      if (!ICON_REGISTRY[category.icon]) {
        throw new Error(`[templates] Category "${category.id}" uses unknown icon "${category.icon}".`);
      }
    }

    const serviceIds = new Set(template.services.map((service) => service.id));

    for (const preset of template.presets) {
      if (preset.items.length === 0) {
        throw new Error(`[templates] Preset "${preset.id}" has no services.`);
      }
      for (const presetItem of preset.items) {
        if (!serviceIds.has(presetItem.serviceId)) {
          throw new Error(
            `[templates] Preset "${preset.id}" references unknown service "${presetItem.serviceId}".`,
          );
        }
      }
    }

    for (const service of template.services) {
      if (seenServices.has(service.id)) {
        throw new Error(`[templates] Duplicate service id "${service.id}".`);
      }
      seenServices.add(service.id);

      if (!categoryIds.has(service.categoryId)) {
        throw new Error(
          `[templates] Service "${service.id}" references unknown category "${service.categoryId}".`,
        );
      }
      if (!ICON_REGISTRY[service.icon]) {
        throw new Error(`[templates] Service "${service.id}" uses unknown icon "${service.icon}".`);
      }
      if (!COLOR_PALETTES[service.color]) {
        throw new Error(`[templates] Service "${service.id}" uses unknown colour "${service.color}".`);
      }
    }
  }
}
